import React, { useEffect } from 'react';
import { Leaf, Recycle, Building, Calendar, Droplets, Wind } from 'lucide-react';
import { campaignData } from '../mock';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { AnimatedCounter } from '../components/AnimatedCounter';
import { ContactForm } from '../components/ContactForm';

const iconMap = {
  leaf: Leaf,
  recycle: Recycle,
  building: Building,
  calendar: Calendar
};

export const Landing = () => {
  useEffect(() => {
    // Smooth scroll animation on section visibility
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, observerOptions);

    // Observe all sections
    const sections = document.querySelectorAll('.fade-in-section');
    sections.forEach((section) => observer.observe(section));

    // Header scroll effect
    const header = document.querySelector('.header');
    const handleScroll = () => {
      if (window.scrollY > 50) {
        header?.classList.add('scrolled');
      } else {
        header?.classList.remove('scrolled');
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      sections.forEach((section) => observer.unobserve(section));
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleCTAClick = (action) => {
    console.log(`CTA clicked: ${action}`);
    // Mock action - will be connected to backend later
  };

  return (
    <div className="landing-container">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <img src="https://customer-assets.emergentagent.com/job_0675c909-9617-4957-a64e-51138db4fa5c/artifacts/w09iox2y_lpsp.png" alt="Lebanese Paper Straw Project" className="logo-image" />
            <span className="logo-text">Lebanese Paper Straw Project</span>
          </div>
          <nav className="nav">
            <a href="#impact" className="nav-link">Impact</a>
            <a href="#about" className="nav-link">About</a>
            <a href="#join" className="nav-link">Join Us</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">
            <Leaf className="badge-icon" />
            <span>Campaign Success Story</span>
          </div>
          <h1 className="hero-title">{campaignData.hero.title}</h1>
          <p className="hero-subtitle">{campaignData.hero.subtitle}</p>
          <p className="hero-description">{campaignData.hero.description}</p>
          <div className="hero-buttons">
            <Button 
              className="btn-primary"
              onClick={() => handleCTAClick('learn-more')}
            >
              Learn More
            </Button>
            <Button 
              variant="outline" 
              className="btn-secondary"
              onClick={() => handleCTAClick('view-impact')}
            >
              View Impact
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section fade-in-section" id="impact">
        <div className="stats-grid">
          {campaignData.stats.map((stat, index) => {
            const IconComponent = iconMap[stat.icon];
            // Extract number and suffix (kg, +, or text like "Months")
            const match = stat.number.match(/^([\d,]+)(.*)$/);
            const numberPart = match ? match[1].replace(/,/g, '') : null;
            const suffixPart = match ? match[2] : stat.number;
            
            return (
              <Card 
                key={stat.id} 
                className="stat-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="stat-icon-wrapper">
                  <IconComponent className="stat-icon" />
                </div>
                <h3 className="stat-number">
                  {numberPart && /^\d+$/.test(numberPart) ? (
                    <AnimatedCounter end={numberPart} suffix={suffixPart} duration={2000} />
                  ) : (
                    stat.number
                  )}
                </h3>
                <p className="stat-label">{stat.label}</p>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Impact Points Section */}
      <section className="impact-section fade-in-section">
        <h2 className="section-title">{campaignData.impact.title}</h2>
        <div className="impact-grid">
          {campaignData.impact.points.map((point, index) => (
            <Card 
              key={point.id} 
              className="impact-card"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <h3 className="impact-card-title">{point.title}</h3>
              <p className="impact-card-description">{point.description}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* About Section */}
      <section className="about-section fade-in-section" id="about">
        <div className="about-container">
          <h2 className="section-title">{campaignData.about.title}</h2>
          <div className="about-content">
            {campaignData.about.content.map((paragraph, index) => (
              <p 
                key={index} 
                className="about-paragraph"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section fade-in-section" id="join">
        <div className="cta-content">
          <div className="cta-decorative-icons">
            <Droplets className="cta-icon" />
            <Wind className="cta-icon" />
            <Leaf className="cta-icon" />
          </div>
          <h2 className="cta-title">{campaignData.cta.title}</h2>
          <p className="cta-description">{campaignData.cta.description}</p>
          
          <div className="contact-form-wrapper">
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        {/* Official Patronage */}
        <div className="footer-patronage">
          <p className="patronage-text">With Official Patronage From</p>
          <img 
            src="https://customer-assets.emergentagent.com/job_0675c909-9617-4957-a64e-51138db4fa5c/artifacts/q7pd4iin_Lebanon_Logo.png" 
            alt="Ministry of Tourism - Lebanon" 
            className="ministry-logo"
          />
        </div>

        <div className="footer-content">
          <div className="footer-section">
            <div className="footer-logo">
              <img src="https://customer-assets.emergentagent.com/job_0675c909-9617-4957-a64e-51138db4fa5c/artifacts/w09iox2y_lpsp.png" alt="Lebanese Paper Straw Project" className="footer-logo-image" />
              <span className="footer-logo-text">Lebanese Paper Straw Project</span>
            </div>
            <p className="footer-tagline">Building a sustainable future, one straw at a time.</p>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Campaign</h4>
            <ul className="footer-links">
              <li><a href="#impact">Our Impact</a></li>
              <li><a href="#about">About Us</a></li>
              <li><a href="#join">Get Involved</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">Contact</h4>
            <p className="footer-text">info@lebpaperstraws.org</p>
            <p className="footer-text">Join our mailing list</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p className="footer-copyright">© 2024 Lebanese Paper Straw Project. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;